import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import type { OfferProbability } from "@/hooks/use-offers";
import { OFFER_PROBABILITY_LABELS } from "@/lib/offer-pipeline-calc";

const PROBABILITY_OPTIONS: OfferProbability[] = ["low", "likely", "very_likely"];

interface Props {
  probability: OfferProbability | null;
  /** ISO-dato (YYYY-MM-DD) eller null. */
  validUntil: string | null;
  readOnly: boolean;
  onProbabilityChange: (p: OfferProbability) => void;
  onValidUntilChange: (date: string | null) => void;
  /** "compact" = mobil-header (kun kategori, ingen dato). "full" = desktop-header. */
  variant?: "compact" | "full";
}

/**
 * VIB-1304: kategori-select + gyldighetsdato-input for tilbudseditoren.
 * Utskilt fra OfferEditHeader (allerede ~310 linjer) som egen komponent for
 * å holde headeren under filstørrelsesgrensa — kun bak feature_offer_pipeline
 * (gates i OfferEditPage/OfferEditHeader, ikke her).
 */
export function OfferPipelineControls({
  probability,
  validUntil,
  readOnly,
  onProbabilityChange,
  onValidUntilChange,
  variant = "full",
}: Props) {
  if (readOnly) return null;

  return (
    <div className={cn("flex items-center gap-1.5", variant === "compact" && "shrink-0")}>
      <Select value={probability ?? undefined} onValueChange={(v) => onProbabilityChange(v as OfferProbability)}>
        <SelectTrigger className={cn("h-8 text-xs", variant === "compact" ? "w-[110px]" : "w-[160px]")}>
          <SelectValue placeholder="Sannsynlighet" />
        </SelectTrigger>
        <SelectContent>
          {PROBABILITY_OPTIONS.map((p) => (
            <SelectItem key={p} value={p}>
              {OFFER_PROBABILITY_LABELS[p]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {variant === "full" && (
        <Input
          type="date"
          value={validUntil ?? ""}
          onChange={(e) => onValidUntilChange(e.target.value || null)}
          className="h-8 w-[150px] text-xs"
          aria-label="Gyldig til"
          title="Gyldig til"
        />
      )}
    </div>
  );
}
